/**
 * Base API helper — shared fetch wrapper with consistent error handling.
 * All feature-specific API modules should use these helpers.
 */

const SERVICE_BASE_URL = process.env.REACT_APP_SERVICE_BASE_URL || '';

function buildUrl(url) {
  if (SERVICE_BASE_URL && url.startsWith('/')) {
    return `${SERVICE_BASE_URL}${url}`;
  }
  return url;
}

/**
 * Parses an error response body into a user-friendly message string.
 */
export async function parseErrorResponse(response, fallback = 'An unexpected error occurred.') {
  try {
    const body = await response.json();
    if (body.errors) return Object.values(body.errors).flat().join(' ');
    return body.title || body.detail || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Performs a JSON GET request and returns the parsed body.
 * Throws an error string on failure.
 */
export async function apiGet(url) {
  const response = await fetch(buildUrl(url), { credentials: 'include' });
  if (!response.ok) throw await parseErrorResponse(response, 'Failed to load data.');
  return response.json();
}

/**
 * Reads the download filename from a Content-Disposition header, if present.
 * Handles both `filename="x.docx"` and RFC 5987 `filename*=UTF-8''x.docx`.
 */
function filenameFromDisposition(disposition) {
  if (!disposition) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) {
    try { return decodeURIComponent(utf8[1]); } catch { return utf8[1]; }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : null;
}

/**
 * GETs a binary file and saves it to disk via a temporary object URL.
 * Returns { ok, error } so callers can surface a failure inline.
 */
export async function apiDownload(url, fallbackFileName = 'download') {
  const response = await fetch(buildUrl(url), { credentials: 'include' });
  if (!response.ok) return { ok: false, error: await parseErrorResponse(response, 'Failed to download the file.') };

  const blob = await response.blob();
  const fileName = filenameFromDisposition(response.headers.get('Content-Disposition')) || fallbackFileName;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
  return { ok: true };
}

/**
 * Performs a JSON POST request and returns { ok, data, error }.
 * Optional opts.signal threads an AbortSignal through so callers can cancel.
 */
export async function apiPost(url, body, opts = {}) {
  const response = await fetch(buildUrl(url), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body != null ? JSON.stringify(body) : undefined,
    signal: opts.signal,
  });
  if (response.ok) {
    const text = await response.text();
    return { ok: true, data: text ? JSON.parse(text) : null };
  }
  return { ok: false, error: await parseErrorResponse(response) };
}

/**
 * Performs a JSON PUT request and returns { ok, error }.
 */
export async function apiPut(url, body) {
  const response = await fetch(buildUrl(url), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (response.ok) return { ok: true };
  return { ok: false, error: await parseErrorResponse(response) };
}

/**
 * Performs a DELETE request and returns { ok, error }.
 */
export async function apiDelete(url) {
  const response = await fetch(buildUrl(url), { method: 'DELETE', credentials: 'include' });
  if (response.ok) return { ok: true };
  return { ok: false, error: await parseErrorResponse(response, 'Failed to delete.') };
}

/**
 * Performs a PATCH request and returns { ok, error }.
 */
export async function apiPatch(url, body) {
  const response = await fetch(buildUrl(url), {
    method: 'PATCH',
    headers: body != null ? { 'Content-Type': 'application/json' } : undefined,
    credentials: 'include',
    body: body != null ? JSON.stringify(body) : undefined,
  });
  if (response.ok) return { ok: true };
  return { ok: false, error: await parseErrorResponse(response) };
}
